import React, { Component } from 'react'

import { ModalActionTypes } from '@acorns/web-components'
import {
  AuthStrategy,
  ClientAuthMethod,
  ClientPlatform,
  GenericAnalyticsClient,
  HttpHeader,
  inferAuthHeaders,
  read,
  Type,
  withAnalyticsClient,
} from '@acorns/web-utils'
import { ApolloProvider } from '@apollo/react-hooks'

import { persistCache } from 'apollo-cache-persist'
import ApolloClient from 'apollo-client'
import { ApolloLink, Operation, from } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import { setContext } from 'apollo-link-context'
import DebounceLink from 'apollo-link-debounce'
import { onError } from 'apollo-link-error'
import { isMobileOnly } from 'react-device-detect'
import { path } from 'ramda'
import { compose } from 'recompose'
import UAParser from 'ua-parser-js'

import { ACORNS_HELLO_FLOW, APP_ID, USER_UUID } from 'app-constants'
import CenteredSpinner from 'components/CenteredSpinner'
import platform from 'components/Trackers/platform'
import { createCache } from './apolloCache'
import mutationResolvers from '../../graphql/resolvers/mutation'
import { typeDefs } from '../../graphql/schema'
import {
  IErrorDetails,
  IErrorPayload,
  hasServiceUnavailableError,
  hasUnauthenticatedError,
  withApolloErrorModal,
} from 'utils/Apollo'
import Environment, { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { getFlow, getFunnelName } from 'utils/FlowProvider/utils'
import { isDynamicFunnelEnabled, retrieveMetadata } from 'utils/FunnelContext/utils'
import {
  debouncedRefreshSessionCookies,
  SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY,
} from 'utils/auth'
import { datadogRum } from '@datadog/browser-rum'

const MOBILE_CONTEXT_HEADER = 'x-client-web-mobile-context'

type ProviderProps = {
  closeErrorModal: () => void
  env: Environment
  getCustomHandler: GetCustomHandlerType
  showErrorModal: (details: IErrorDetails) => void
  updateErrorDetails: (newErrorDetails: object) => void
  analytics: GenericAnalyticsClient
}

type GetCustomHandlerType = (name: string) => (error: IErrorPayload) => IErrorDetails | boolean

export class ApolloProviderWrapper extends Component<
  ProviderProps,
  { loading: boolean; client: any; udid: string }
> {
  constructor(props) {
    super(props)

    this.state = {
      loading: true,
      client: undefined,
      udid: '',
    }

    this.createApolloClient()
      .then((client) =>
        this.setState({
          client,
          loading: false,
          udid: this.props.analytics.getAnonymousId(),
        }),
      )
      .catch((error) =>
        sendToSentry(error, {
          name: 'createApolloClient() ERROR',
          message:
            'App will not render because Apollo Client is not instantiated. This is a possible SEV-1',
        }),
      )
  }

  public render() {
    return this.state.loading ? (
      <CenteredSpinner />
    ) : (
      <ApolloProvider client={this.state.client}>{this.props.children}</ApolloProvider>
    )
  }

  protected async createApolloClient() {
    const cache = createCache()

    try {
      await persistCache({
        cache,
        storage: window.sessionStorage,
      })
    } catch (error) {
      sendToSentry(error, {
        name: 'Apollo Persist Cache Error',
        message: 'Attempted to setup Apollo Client with persistCache()',
      })
    }

    // Note: All GraphQL and network errors get run through this
    // so we can take appropriate action
    const errorLink = this.createErrorLink()

    const networkCallbackLink = this.createNetworkCallback((operation) => {
      // If a session refresh has been queued, debounce it until the GraphQL operations have settled down
      if (
        window.sessionStorage.getItem(SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY) === 'true' &&
        operation.operationName !== 'RefreshAuthTokens'
      ) {
        debouncedRefreshSessionCookies(this.state.client)
      }
    })

    // Note: Batch queries executed within an interval together
    // in a single network request
    const batchHttpLink = new BatchHttpLink({
      uri: this.props.env.get('graphql'),
      credentials: 'include',
    })

    const headers = this.setHeaders()

    const setAuthorizationLink = setContext((request) => {
      const authHeaders = inferAuthHeaders()
      return {
        headers: {
          ...headers,
          ...authHeaders,
          // this header supports the pricing interstitial experiment to test the default tier
          // we can either add this to specific operations or the entire registration funnel
          // adding it to the specific operation seemed the cleanest without having to track which funnel
          // we're in
          ...(request.operationName === 'DynamicSubscriptionPricingContent' && {
            'X-Client-Registration-Context': 'true',
          }),
          'X-Client-UDID': this.state.udid,
        },
      }
    })

    // Note: Performs debounce logic on queries that have a `debounceKey` configured
    const DEBOUNCE_TIMEOUT = 250
    const debounceLink = new DebounceLink(DEBOUNCE_TIMEOUT) as any

    const links = [
      errorLink,
      debounceLink,
      networkCallbackLink,
      setAuthorizationLink,
      batchHttpLink,
    ]

    return new ApolloClient({
      cache,
      link: from(links),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-first',
        },
      },
      typeDefs,
      resolvers: {
        Mutation: mutationResolvers,
      },
    })
  }

  protected setHeaders() {
    const userAgentParser = new UAParser()
    const userAgent = userAgentParser.getResult()
    const release = this.props.env.get('release')

    const customHeaders = {
      Accept: 'application/json, text/plain, */*',
      [HttpHeader.AuthStrategy]: AuthStrategy.Jwt,
      [HttpHeader.ClientAppName]: APP_ID,
      [HttpHeader.ClientBrowserName]: userAgent.browser.name,
      [HttpHeader.ClientBrowserVersion]: userAgent.browser.version,
      [HttpHeader.ClientBuild]: release,
      [HttpHeader.ClientHardware]: userAgent.device.model,
      [HttpHeader.ClientOperatingSystem]: userAgent.os.name,
      [HttpHeader.ClientPlatform]: ClientPlatform.Web,
      ...(isMobileOnly && { [MOBILE_CONTEXT_HEADER]: 'true' }),
    }

    if (
      process.env.BUILD_ENV === 'dev' ||
      process.env.BUILD_ENV === 'staging' ||
      process.env.BUILD_ENV === 'production'
    ) {
      // The HTTP header needs to be set for deployed environments for implicit cookie auth.
      // For local development, make sure BUILD_ENV is not set to one of the above values.
      customHeaders[HttpHeader.ClientAuthMethod] = ClientAuthMethod.Cookies
    }

    return customHeaders
  }

  protected trackError(errorContext: object) {
    return window.analytics.track('Error Received', {
      feature: 'API Error',
      event_description: 'Occurred',
      platform,
      funnel: isDynamicFunnelEnabled() ? retrieveMetadata().name : getFunnelName(getFlow()),
      path: window.location.pathname,
      ...errorContext,
    })
  }

  protected createNetworkCallback(callback: (operation: Operation) => void) {
    return new ApolloLink((operation, forward) => {
      callback(operation)
      return forward(operation)
    })
  }

  /* istanbul ignore next */
  protected createErrorLink() {
    return onError(({ graphQLErrors, networkError, operation }) => {
      const flow = getFlow()
      // Determine the type of error and handle accordingly
      if (
        operation.operationName !== 'IsAuthenticated' &&
        operation.operationName !== 'RegistrationCheck' &&
        window.sessionStorage.getItem(USER_UUID) !== null &&
        hasUnauthenticatedError(graphQLErrors)
      ) {
        if (
          flow === 'laterOnboarding' ||
          flow === 'checkingOnboarding' ||
          flow === 'mocOnboarding' ||
          flow === 'earlyOnboarding' ||
          flow === 'investOnboarding'
        ) {
          // The user is supposed to be already authenticated in the product onboarding flows.
          // If the user is not, then we remove the flow flag and send them back to the beginning.
          window.localStorage.removeItem(ACORNS_HELLO_FLOW)
        }
        this.handleAuthenticationError()
      } else if (hasServiceUnavailableError(graphQLErrors)) {
        this.handleServiceUnavailableError()
      } else {
        this.handleError(graphQLErrors, networkError, operation)
      }
    })
  }

  protected handleAuthenticationError() {
    const handleErrorModal = () => {
      this.props.closeErrorModal()
      window.location.replace(this.props.env.get('loginUrl'))
    }

    // the auth cookie should never be exposed for security but we need to be aware if it's not set
    this.trackError({
      error_message: 'Your request could not be processed because you are not authenticated.',
      hasAuthCookie: !!read(Type.token),
    })
    this.props.showErrorModal({
      isTrap: true,
      title: 'Unauthenticated',
      message: 'Your request could not be processed because you are not authenticated.',
      actions: [
        {
          handler: handleErrorModal,
          label: 'Ok',
          type: ModalActionTypes.primary,
        },
      ],
    })
  }

  protected handleServiceUnavailableError() {
    this.props.showErrorModal({
      title: 'Please Contact Support',
      message:
        'We are unable to process your request online, but our support team is here to help. Please contact us and we will work to resolve this as soon as possible.',
      actions: [
        {
          handler: this.props.closeErrorModal,
          label: 'Cancel',
          type: ModalActionTypes.secondary,
        },
        {
          href: 'https://www.acorns.com/support/contact/',
          label: 'Contact Support',
          type: ModalActionTypes.primary,
        },
      ],
    })
    this.trackError({
      error_message:
        'We are unable to process your request online, but our support team is here to help. Please contact us and we will work to resolve this as soon as possible.',
    })
  }

  protected handleError(graphQLErrors, networkError, operation) {
    this.props.updateErrorDetails({ graphQLErrors, networkError, operation })
    if (graphQLErrors) {
      this.trackError({
        screen: location.pathname,
        operation_name: operation.operationName,
        is_network_error: !window.navigator.onLine,
        networkError,
        error_code: graphQLErrors?.length
          ? graphQLErrors?.[0]?.extensions?.response?.status
          : networkError?.statusCode,
        error_details: graphQLErrors || networkError?.message,
      })

      graphQLErrors.forEach((error) => {
        datadogRum.addError(new Error(`GraphQL Error ${error.path}`), {
          context: error,
        })
      })
    }

    const operationType = path(['query', 'definitions', '0', 'operation'], operation)
    const handler = this.props.getCustomHandler(operation.operationName)

    let errorDetails

    if (typeof handler === 'function') {
      errorDetails = handler({
        graphQLErrors,
        networkError,
      })
    }

    Promise.resolve(errorDetails).then((results) => {
      // Disable this error modal if the handler returns false
      if (typeof results === 'boolean' && !results) {
        return
      }

      if (operationType === 'mutation' || results) {
        this.props.showErrorModal(results)
      }
    })
  }
}

export const enhance = compose(withApolloErrorModal, withEnv, withAnalyticsClient)

export const Provider = enhance(ApolloProviderWrapper)
